import type {
  PlanAppointmentPlanningPreview,
  PlanAppointmentPreview,
  PlanAppointmentSettings,
  PlanAppointmentSourcePreview,
  PlanAppointmentSourceRowContext,
  PlanPropertySummary,
  PlanScheduleEventSummary,
  PlanWorkOrderSummary,
} from "../contract"

type JsonObject = Record<string, unknown>
type PlanningResponse = PlanAppointmentPlanningPreview["planning"]
type PlanningRow = PlanningResponse["rows"][number]
type PlanningAppointment = NonNullable<PlanningRow["appointment"]>
type PlanningRouteAnchor = NonNullable<PlanningAppointment["previous_anchor"]>
type PlanningAvailabilityMatch = NonNullable<PlanningAppointment["customer_availability_match"]>
type PlanningManualSlot = NonNullable<PlanningRow["manual_slot_options"]>[number]
type PlanningResolvedContact = NonNullable<PlanningRow["resolved_appointment_contact"]>
type PlanningWarning = PlanningRow["warnings"][number]
type PlanningCostBenefit = NonNullable<PlanningResponse["cost_benefit"]>
type PlanningRouteProperty = NonNullable<PlanningResponse["route_properties"]>[number]
type PlanningStrategy = NonNullable<PlanningRow["available_planning_strategies"]>[number]

const PLANNING_STRATEGIES = [
  "AUTO",
  "MANUAL",
  "KEEP_EXISTING",
] as const satisfies readonly PlanningStrategy[]

const objectValue = (value: unknown, path: string): JsonObject => {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new Error(`${path} muss ein Objekt sein.`)

  return value as JsonObject
}

const exactKeys = (value: JsonObject, allowed: readonly string[], path: string) => {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key))

  if (unexpected.length)
    throw new Error(`${path} enthält unbekannte Felder: ${unexpected.join(", ")}.`)
}

const text = (value: unknown, path: string) => {
  if (typeof value !== "string" || !value.trim())
    throw new Error(`${path} muss ein nicht-leerer String sein.`)

  return value.trim()
}

const nullableText = (value: unknown, path: string) => {
  if (value === null || value === undefined) return ""

  if (typeof value !== "string") throw new Error(`${path} muss String oder null sein.`)

  return value.trim()
}

const nullableId = (value: unknown, path: string): string | null =>
  nullableText(value, path) || null

const integer = (value: unknown, path: string) => {
  if (typeof value !== "number" || !Number.isInteger(value))
    throw new Error(`${path} muss eine Ganzzahl sein.`)

  return value
}

const number = (value: unknown, path: string) => {
  if (typeof value !== "number" || !Number.isFinite(value))
    throw new Error(`${path} muss eine Zahl sein.`)

  return value
}

const nullableNumber = (value: unknown, path: string): number | null => {
  if (value === null || value === undefined) return null

  return number(value, path)
}

const boolean = (value: unknown, path: string) => {
  if (typeof value !== "boolean") throw new Error(`${path} muss ein Boolean sein.`)

  return value
}

const nullableInteger = (value: unknown, path: string): number | null => {
  if (value === null || value === undefined) return null

  return integer(value, path)
}

const textArray = (value: unknown, path: string) => {
  if (!Array.isArray(value)) throw new Error(`${path} muss ein Array sein.`)

  return value.map((entry, index) => text(entry, `${path}.${index}`))
}

const enumValue = <T extends string>(value: unknown, allowed: readonly T[], path: string): T => {
  const parsed = text(value, path)

  if (!allowed.includes(parsed as T))
    throw new Error(`${path} enthält den unbekannten Wert ${parsed}.`)

  return parsed as T
}

const optionalArray = <T>(
  value: unknown,
  path: string,
  parser: (entry: unknown, entryPath: string) => T,
): T[] | undefined => {
  if (value === undefined) return undefined

  if (!Array.isArray(value)) throw new Error(`${path} muss ein Array sein.`)

  return value.map((entry, index) => parser(entry, `${path}.${index}`))
}

const costBenefit = (value: unknown, path: string): PlanningCostBenefit | null => {
  if (value === null || value === undefined) return null

  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "time_saved_minutes",
      "remaining_effort_minutes",
      "total_credits",
      "cost_breakdown",
      "assumptions",
      "warnings",
    ],
    path,
  )
  const breakdown = optionalArray(
    data.cost_breakdown,
    `${path}.cost_breakdown`,
    (entry, entryPath) => {
      const item = objectValue(entry, entryPath)
      exactKeys(item, ["label", "count", "credits"], entryPath)

      return {
        label: text(item.label, `${entryPath}.label`),
        count: integer(item.count, `${entryPath}.count`),
        credits: number(item.credits, `${entryPath}.credits`),
      }
    },
  )

  return {
    time_saved_minutes: nullableNumber(data.time_saved_minutes, `${path}.time_saved_minutes`),
    remaining_effort_minutes: nullableNumber(
      data.remaining_effort_minutes,
      `${path}.remaining_effort_minutes`,
    ),
    total_credits: nullableNumber(data.total_credits, `${path}.total_credits`),
    ...(breakdown ? { cost_breakdown: breakdown } : {}),
    ...(data.assumptions === undefined
      ? {}
      : { assumptions: textArray(data.assumptions, `${path}.assumptions`) }),
    ...(data.warnings === undefined
      ? {}
      : { warnings: textArray(data.warnings, `${path}.warnings`) }),
  }
}

const routeAnchor = (value: unknown, path: string): PlanningRouteAnchor | null => {
  if (value === null || value === undefined) return null

  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "schedule_event_id",
      "work_order_id",
      "start_at",
      "end_at",
      "employee_id",
      "address_label",
      "title",
      "distance_km",
      "anchor_kind",
      "estimated_travel_minutes",
      "travel_time_estimated",
    ],
    path,
  )
  const anchorKind = nullableId(data.anchor_kind, `${path}.anchor_kind`)

  if (
    anchorKind &&
    ![
      "appointment",
      "external_block",
      "internal_block",
      "absence",
      "office_start",
      "office_end",
    ].includes(anchorKind)
  ) {
    throw new Error(`${path}.anchor_kind enthält den unbekannten Wert ${anchorKind}.`)
  }

  return {
    schedule_event_id: nullableId(data.schedule_event_id, `${path}.schedule_event_id`),
    work_order_id: nullableId(data.work_order_id, `${path}.work_order_id`),
    start_at: nullableId(data.start_at, `${path}.start_at`),
    end_at: nullableId(data.end_at, `${path}.end_at`),
    employee_id: nullableId(data.employee_id, `${path}.employee_id`),
    address_label: nullableId(data.address_label, `${path}.address_label`),
    title: nullableId(data.title, `${path}.title`),
    distance_km: nullableNumber(data.distance_km, `${path}.distance_km`),
    anchor_kind: anchorKind as PlanningRouteAnchor["anchor_kind"],
    estimated_travel_minutes: nullableNumber(
      data.estimated_travel_minutes,
      `${path}.estimated_travel_minutes`,
    ),
    travel_time_estimated:
      data.travel_time_estimated === null || data.travel_time_estimated === undefined
        ? null
        : boolean(data.travel_time_estimated, `${path}.travel_time_estimated`),
  }
}

const availabilityMatch = (value: unknown, path: string): PlanningAvailabilityMatch | null => {
  if (value === null || value === undefined) return null

  const data = objectValue(value, path)
  exactKeys(
    data,
    ["tier", "matched_window_start_at", "matched_window_end_at", "minutes_outside"],
    path,
  )

  return {
    tier: enumValue(
      data.tier,
      ["NO_PREFERENCE", "INSIDE_WINDOW", "PARTIAL_OVERLAP", "NEAR_WINDOW", "OUTSIDE_WINDOW"],
      `${path}.tier`,
    ),
    matched_window_start_at: nullableId(
      data.matched_window_start_at,
      `${path}.matched_window_start_at`,
    ),
    matched_window_end_at: nullableId(data.matched_window_end_at, `${path}.matched_window_end_at`),
    minutes_outside: nullableNumber(data.minutes_outside, `${path}.minutes_outside`),
  }
}

const planningAppointment = (value: unknown, path: string): PlanningAppointment | null => {
  if (value === null || value === undefined) return null

  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "start_at",
      "end_at",
      "employee_id",
      "employee_name",
      "flex_type",
      "timeframe_start_at",
      "timeframe_end_at",
      "customer_window_start_at",
      "customer_window_end_at",
      "customer_availability_match",
      "previous_anchor",
      "next_anchor",
    ],
    path,
  )

  return {
    start_at: text(data.start_at, `${path}.start_at`),
    end_at: text(data.end_at, `${path}.end_at`),
    employee_id: text(data.employee_id, `${path}.employee_id`),
    employee_name: nullableId(data.employee_name, `${path}.employee_name`),
    flex_type: text(data.flex_type, `${path}.flex_type`),
    timeframe_start_at: nullableId(data.timeframe_start_at, `${path}.timeframe_start_at`),
    timeframe_end_at: nullableId(data.timeframe_end_at, `${path}.timeframe_end_at`),
    customer_window_start_at: nullableId(
      data.customer_window_start_at,
      `${path}.customer_window_start_at`,
    ),
    customer_window_end_at: nullableId(
      data.customer_window_end_at,
      `${path}.customer_window_end_at`,
    ),
    customer_availability_match: availabilityMatch(
      data.customer_availability_match,
      `${path}.customer_availability_match`,
    ),
    previous_anchor: routeAnchor(data.previous_anchor, `${path}.previous_anchor`),
    next_anchor: routeAnchor(data.next_anchor, `${path}.next_anchor`),
  }
}

const manualSlot = (value: unknown, path: string): PlanningManualSlot => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "start_at",
      "end_at",
      "employee_id",
      "flex_type",
      "timeframe_start_at",
      "timeframe_end_at",
      "label",
    ],
    path,
  )

  return {
    start_at: text(data.start_at, `${path}.start_at`),
    end_at: text(data.end_at, `${path}.end_at`),
    employee_id: text(data.employee_id, `${path}.employee_id`),
    flex_type: enumValue(data.flex_type, ["HARD", "SOFT"], `${path}.flex_type`),
    timeframe_start_at: nullableId(data.timeframe_start_at, `${path}.timeframe_start_at`),
    timeframe_end_at: nullableId(data.timeframe_end_at, `${path}.timeframe_end_at`),
    label: nullableId(data.label, `${path}.label`),
  }
}

const resolvedContact = (value: unknown, path: string): PlanningResolvedContact | null => {
  if (value === null || value === undefined) return null

  const data = objectValue(value, path)
  exactKeys(data, ["customer_id", "display_name", "role", "scope", "source"], path)
  const scope = nullableId(data.scope, `${path}.scope`)

  if (scope && scope !== "UNIT" && scope !== "BUILDING") {
    throw new Error(`${path}.scope enthält den unbekannten Wert ${scope}.`)
  }

  return {
    customer_id: nullableId(data.customer_id, `${path}.customer_id`),
    display_name: nullableId(data.display_name, `${path}.display_name`),
    role: nullableId(data.role, `${path}.role`),
    scope: scope as PlanningResolvedContact["scope"],
    source: enumValue(data.source, ["USER_SELECTED", "AUTO_DERIVED", "NONE"], `${path}.source`),
  }
}

const warning = (value: unknown, path: string): PlanningWarning => {
  const data = objectValue(value, path)
  exactKeys(data, ["code", "severity", "message"], path)

  return {
    code: text(data.code, `${path}.code`),
    severity: enumValue(data.severity, ["info", "warning", "error"], `${path}.severity`),
    message: text(data.message, `${path}.message`),
  }
}

const routeProperty = (value: unknown, path: string): PlanningRouteProperty => {
  const data = objectValue(value, path)
  exactKeys(data, ["property_id", "geo_lat", "geo_lng"], path)

  return {
    property_id: text(data.property_id, `${path}.property_id`),
    geo_lat: nullableNumber(data.geo_lat, `${path}.geo_lat`),
    geo_lng: nullableNumber(data.geo_lng, `${path}.geo_lng`),
  }
}

const commitTarget = (value: unknown, path: string): PlanningRow["commit_target"] => {
  if (value === null || value === undefined) return null

  const data = objectValue(value, path)
  const kind = enumValue(data.kind, ["SET", "KEEP_EXISTING"], `${path}.kind`)

  if (kind === "KEEP_EXISTING") {
    exactKeys(data, ["kind", "schedule_event_id"], path)

    return { kind, schedule_event_id: text(data.schedule_event_id, `${path}.schedule_event_id`) }
  }

  exactKeys(data, ["kind", "expected_active_schedule_event_id", "appointment"], path)

  if (!("expected_active_schedule_event_id" in data))
    throw new Error(`${path}.expected_active_schedule_event_id fehlt.`)

  const appointment = objectValue(data.appointment, `${path}.appointment`)
  exactKeys(
    appointment,
    ["employee_id", "flex_type", "start_at", "end_at", "timeframe_start_at", "timeframe_end_at"],
    `${path}.appointment`,
  )

  return {
    kind,
    expected_active_schedule_event_id: nullableId(
      data.expected_active_schedule_event_id,
      `${path}.expected_active_schedule_event_id`,
    ),
    appointment: {
      employee_id: text(appointment.employee_id, `${path}.appointment.employee_id`),
      flex_type: enumValue(
        appointment.flex_type,
        ["HARD", "SOFT"],
        `${path}.appointment.flex_type`,
      ),
      start_at: text(appointment.start_at, `${path}.appointment.start_at`),
      end_at: text(appointment.end_at, `${path}.appointment.end_at`),
      timeframe_start_at: text(
        appointment.timeframe_start_at,
        `${path}.appointment.timeframe_start_at`,
      ),
      timeframe_end_at: text(appointment.timeframe_end_at, `${path}.appointment.timeframe_end_at`),
    },
  }
}

const planningRow = (value: unknown, path: string): PlanningRow => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "commit_target",
      "client_row_id",
      "status",
      "appointment",
      "available_planning_strategies",
      "active_schedule_event",
      "effective_planning_strategy",
      "manual_slot_options",
      "resolved_appointment_contact",
      "warnings",
    ],
    path,
  )
  const strategies = optionalArray(
    data.available_planning_strategies,
    `${path}.available_planning_strategies`,
    (entry, entryPath) => enumValue(entry, PLANNING_STRATEGIES, entryPath),
  )
  const manualSlots = optionalArray(
    data.manual_slot_options,
    `${path}.manual_slot_options`,
    manualSlot,
  )

  if (!Array.isArray(data.warnings)) throw new Error(`${path}.warnings muss ein Array sein.`)

  return {
    commit_target: commitTarget(data.commit_target, `${path}.commit_target`),
    client_row_id: text(data.client_row_id, `${path}.client_row_id`),
    status: enumValue(data.status, ["planned", "unplanned"], `${path}.status`),
    appointment: planningAppointment(data.appointment, `${path}.appointment`),
    ...(strategies ? { available_planning_strategies: strategies } : {}),
    active_schedule_event: planningAppointment(
      data.active_schedule_event,
      `${path}.active_schedule_event`,
    ),
    effective_planning_strategy:
      data.effective_planning_strategy === null || data.effective_planning_strategy === undefined
        ? undefined
        : enumValue(
            data.effective_planning_strategy,
            PLANNING_STRATEGIES,
            `${path}.effective_planning_strategy`,
          ),
    ...(manualSlots ? { manual_slot_options: manualSlots } : {}),
    resolved_appointment_contact: resolvedContact(
      data.resolved_appointment_contact,
      `${path}.resolved_appointment_contact`,
    ),
    warnings: data.warnings.map((entry, index) => warning(entry, `${path}.warnings.${index}`)),
  }
}

const workOrder = (value: unknown, path: string): PlanWorkOrderSummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "id",
      "property_id",
      "property_unit_id",
      "property_unit_label",
      "order_number",
      "order_type_ids",
      "order_type_label",
      "due_date",
      "estimated_duration_min",
      "assigned_employee_id",
      "allowed_employee_ids",
      "current_schedule_event_id",
      "status",
    ],
    path,
  )

  return {
    id: text(data.id, `${path}.id`),
    propertyId: text(data.property_id, `${path}.property_id`),
    propertyUnitId: nullableId(data.property_unit_id, `${path}.property_unit_id`),
    propertyUnitLabel: nullableText(data.property_unit_label, `${path}.property_unit_label`),
    orderNumber: nullableText(data.order_number, `${path}.order_number`),
    orderTypeIds: textArray(data.order_type_ids, `${path}.order_type_ids`),
    orderTypeLabel: nullableText(data.order_type_label, `${path}.order_type_label`),
    dueDate: nullableId(data.due_date, `${path}.due_date`),
    estimatedDurationMin: nullableInteger(
      data.estimated_duration_min,
      `${path}.estimated_duration_min`,
    ),
    assignedEmployeeId: nullableId(data.assigned_employee_id, `${path}.assigned_employee_id`),
    allowedEmployeeIds: textArray(data.allowed_employee_ids, `${path}.allowed_employee_ids`),
    currentScheduleEventId: nullableId(
      data.current_schedule_event_id,
      `${path}.current_schedule_event_id`,
    ),
    status: text(data.status, `${path}.status`),
  }
}

const property = (value: unknown, path: string): PlanPropertySummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "id",
      "street",
      "house_number",
      "postal_code",
      "city",
      "country",
      "address_extra",
      "address_label",
    ],
    path,
  )

  return {
    id: text(data.id, `${path}.id`),
    street: text(data.street, `${path}.street`),
    houseNumber: nullableText(data.house_number, `${path}.house_number`),
    postalCode: nullableText(data.postal_code, `${path}.postal_code`),
    city: nullableText(data.city, `${path}.city`),
    country: nullableText(data.country, `${path}.country`),
    addressExtra: nullableText(data.address_extra, `${path}.address_extra`),
    addressLabel: text(data.address_label, `${path}.address_label`),
  }
}

const scheduleEvent = (value: unknown, path: string): PlanScheduleEventSummary => {
  const data = objectValue(value, path)
  exactKeys(
    data,
    [
      "id",
      "work_order_id",
      "property_id",
      "employee_id",
      "employee_name",
      "start_at",
      "end_at",
      "status",
      "flex_type",
      "timeframe_start_at",
      "timeframe_end_at",
      "customer_window_start_at",
      "customer_window_end_at",
    ],
    path,
  )

  return {
    id: text(data.id, `${path}.id`),
    workOrderId: nullableId(data.work_order_id, `${path}.work_order_id`),
    propertyId: nullableId(data.property_id, `${path}.property_id`),
    employeeId: nullableId(data.employee_id, `${path}.employee_id`),
    employeeName: nullableText(data.employee_name, `${path}.employee_name`),
    startAt: text(data.start_at, `${path}.start_at`),
    endAt: text(data.end_at, `${path}.end_at`),
    status: text(data.status, `${path}.status`),
    flexType: nullableId(data.flex_type, `${path}.flex_type`),
    timeframeStartAt: nullableId(data.timeframe_start_at, `${path}.timeframe_start_at`),
    timeframeEndAt: nullableId(data.timeframe_end_at, `${path}.timeframe_end_at`),
    customerWindowStartAt: nullableId(
      data.customer_window_start_at,
      `${path}.customer_window_start_at`,
    ),
    customerWindowEndAt: nullableId(data.customer_window_end_at, `${path}.customer_window_end_at`),
  }
}

const keyed = <T extends { id: string }>(
  value: unknown,
  path: string,
  parser: (entry: unknown, entryPath: string) => T,
): Record<string, T> =>
  Object.fromEntries(
    Object.entries(objectValue(value, path)).map(([id, entry]) => {
      const parsed = parser(entry, `${path}.${id}`)

      if (parsed.id !== id)
        throw new Error(`${path}.${id}.id stimmt nicht mit dem Map-Key überein.`)

      return [id, parsed]
    }),
  )

const settings = (value: unknown, path: string): PlanAppointmentSettings => {
  const data = objectValue(value, path)
  exactKeys(data, ["flex_slot_minutes", "flex_slots_per_day"], path)

  return {
    flex_slot_minutes: integer(data.flex_slot_minutes, `${path}.flex_slot_minutes`),
    flex_slots_per_day: integer(data.flex_slots_per_day, `${path}.flex_slots_per_day`),
  }
}

const sourcePreview = (root: JsonObject): PlanAppointmentSourcePreview => {
  exactKeys(
    root,
    ["mode", "row_context_by_action_case_id", "row_errors_by_action_case_id", "shared"],
    "payload",
  )
  const rows = objectValue(
    root.row_context_by_action_case_id,
    "payload.row_context_by_action_case_id",
  )
  const errors = objectValue(
    root.row_errors_by_action_case_id,
    "payload.row_errors_by_action_case_id",
  )
  const shared = objectValue(root.shared, "payload.shared")
  exactKeys(
    shared,
    ["work_orders_by_id", "properties_by_id", "schedule_events_by_id", "planning_settings"],
    "payload.shared",
  )

  return {
    mode: "source",
    rowContextByActionCaseId: Object.fromEntries(
      Object.entries(rows).map(([id, value]) => {
        const row = objectValue(value, `payload.row_context_by_action_case_id.${id}`)
        exactKeys(
          row,
          [
            "work_order_id",
            "property_id",
            "requester_customer_id",
            "active_schedule_event_id",
            "source_schedule_event_id",
            "canceled_schedule_event_id",
            "default_planning_strategy",
          ],
          `payload.row_context_by_action_case_id.${id}`,
        )
        const strategy = text(
          row.default_planning_strategy,
          `payload.row_context_by_action_case_id.${id}.default_planning_strategy`,
        )

        if (strategy !== "AUTO" && strategy !== "KEEP_EXISTING")
          throw new Error(
            `payload.row_context_by_action_case_id.${id}.default_planning_strategy ist ungültig.`,
          )

        return [
          id,
          {
            workOrderId: text(
              row.work_order_id,
              `payload.row_context_by_action_case_id.${id}.work_order_id`,
            ),
            propertyId: text(
              row.property_id,
              `payload.row_context_by_action_case_id.${id}.property_id`,
            ),
            requesterCustomerId: text(
              row.requester_customer_id,
              `payload.row_context_by_action_case_id.${id}.requester_customer_id`,
            ),
            activeScheduleEventId: nullableId(
              row.active_schedule_event_id,
              `payload.row_context_by_action_case_id.${id}.active_schedule_event_id`,
            ),
            sourceScheduleEventId: nullableId(
              row.source_schedule_event_id,
              `payload.row_context_by_action_case_id.${id}.source_schedule_event_id`,
            ),
            canceledScheduleEventId: nullableId(
              row.canceled_schedule_event_id,
              `payload.row_context_by_action_case_id.${id}.canceled_schedule_event_id`,
            ),
            defaultPlanningStrategy: strategy,
          } satisfies PlanAppointmentSourceRowContext,
        ]
      }),
    ),
    rowErrorsByActionCaseId: Object.fromEntries(
      Object.entries(errors).map(([id, value]) => [
        id,
        text(value, `payload.row_errors_by_action_case_id.${id}`),
      ]),
    ),
    workOrdersById: keyed(shared.work_orders_by_id, "payload.shared.work_orders_by_id", workOrder),
    propertiesById: keyed(shared.properties_by_id, "payload.shared.properties_by_id", property),
    scheduleEventsById: keyed(
      shared.schedule_events_by_id,
      "payload.shared.schedule_events_by_id",
      scheduleEvent,
    ),
    planningSettings: settings(shared.planning_settings, "payload.shared.planning_settings"),
  }
}

const planningPreview = (root: JsonObject): PlanAppointmentPlanningPreview => {
  exactKeys(root, ["mode", "planning", "row_errors_by_action_case_id"], "payload")
  const planning = objectValue(root.planning, "payload.planning")
  const errors = objectValue(
    root.row_errors_by_action_case_id,
    "payload.row_errors_by_action_case_id",
  )
  exactKeys(
    planning,
    [
      "rows",
      "route_properties",
      "summary",
      "optimization_applied",
      "optimization_warning",
      "changed_row_count",
      "cost_benefit",
    ],
    "payload.planning",
  )

  if (!Array.isArray(planning.rows)) throw new Error("payload.planning.rows muss ein Array sein.")

  const routeProperties = optionalArray(
    planning.route_properties,
    "payload.planning.route_properties",
    routeProperty,
  )
  const summary = objectValue(planning.summary, "payload.planning.summary")
  exactKeys(
    summary,
    ["total_rows", "planned_rows", "unplanned_rows", "warning_rows", "error_rows"],
    "payload.planning.summary",
  )

  return {
    mode: "planning",
    planning: {
      rows: planning.rows.map((entry, index) =>
        planningRow(entry, `payload.planning.rows.${index}`),
      ),
      ...(routeProperties ? { route_properties: routeProperties } : {}),
      summary: {
        total_rows: integer(summary.total_rows, "payload.planning.summary.total_rows"),
        planned_rows: integer(summary.planned_rows, "payload.planning.summary.planned_rows"),
        unplanned_rows: integer(summary.unplanned_rows, "payload.planning.summary.unplanned_rows"),
        warning_rows: integer(summary.warning_rows, "payload.planning.summary.warning_rows"),
        error_rows: integer(summary.error_rows, "payload.planning.summary.error_rows"),
      },
      optimization_applied: boolean(
        planning.optimization_applied,
        "payload.planning.optimization_applied",
      ),
      optimization_warning: nullableId(
        planning.optimization_warning,
        "payload.planning.optimization_warning",
      ),
      changed_row_count: integer(planning.changed_row_count, "payload.planning.changed_row_count"),
      cost_benefit: costBenefit(planning.cost_benefit, "payload.planning.cost_benefit"),
    },
    rowErrorsByActionCaseId: Object.fromEntries(
      Object.entries(errors).map(([id, value]) => [
        id,
        text(value, `payload.row_errors_by_action_case_id.${id}`),
      ]),
    ),
  }
}

export const parsePlanAppointmentPreview = (payload: unknown): PlanAppointmentPreview => {
  const root = objectValue(payload, "payload")
  const mode = text(root.mode, "payload.mode")

  if (mode === "source") return sourcePreview(root)

  if (mode === "planning") return planningPreview(root)

  throw new Error(`payload.mode enthält den unbekannten Wert ${mode}.`)
}
